import { useCallback, useEffect, useRef } from 'react';
import { ChatMessage, AIExpert } from '@garbera/shared';
import { useChatMessages } from './useChatMessages';
import { useChatSession } from './useChatSession';
import { useChatFileUpload } from './useChatFileUpload';
import { UseChatOptions, ChatEventCallbacks } from './types';

/**
 * 統合されたチャット機能フック - リファクタリング後
 * 378行から大幅に削減し、専門フックを組み合わせて使用
 */
export const useChat = (
  options: UseChatOptions = {},
  callbacks?: ChatEventCallbacks
) => {
  const { expertId, sessionId, enableMockResponses = false } = options;

  // 専門フックを使用
  const messages = useChatMessages(enableMockResponses);
  const session = useChatSession();
  const fileUpload = useChatFileUpload();

  // 現在読み込み中のセッションIDをトラッキング
  const loadingSessionRef = useRef<number | null>(null);
  
  // 初期化処理 - sessionIdが変更された時のみ実行
  useEffect(() => {
    if (!sessionId) return;
    
    // 既に同じセッションを読み込み中の場合はスキップ
    if (loadingSessionRef.current === sessionId) {
      console.log('Session already loading, skipping:', sessionId);
      return;
    }
    
    console.log('Loading chat history for session:', sessionId);
    loadingSessionRef.current = sessionId;
    
    const loadData = async () => {
      try {
        // セッション情報を読み込み
        const { session: loadedSession } = await session.loadChatHistory(sessionId);
        
        if (loadedSession) {
          console.log('Loaded session:', loadedSession);
          
          // メッセージ履歴を読み込み
          await messages.loadMessages(sessionId);
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
      } finally {
        // 読み込み完了後にリセット
        loadingSessionRef.current = null;
      }
    };
    
    loadData();
  }, [sessionId]); // sessionIdのみに依存

  // 統合されたメッセージ送信機能
  const sendMessage = useCallback(async (content: string, files?: File[]) => {
    console.log('useChat.sendMessage called with:', { content, filesCount: files?.length || 0, hasSession: !!session.session, hasExpert: !!session.currentExpert });
    
    if (!session.session || !session.currentExpert) {
      console.log('Missing session or expert:', { session: session.session, expert: session.currentExpert });
      messages.clearError();
      return;
    }

    try {
      // ファイルがある場合はアップロード
      let uploadedFiles: Array<{ file: File; url: string; id: string }> = [];
      if (files && files.length > 0) {
        const { uploadedFiles: uploaded, errors } = await fileUpload.uploadFiles(files);
        uploadedFiles = uploaded;
        
        if (errors.length > 0) {
          console.warn('Some files failed to upload:', errors);
        }
      }

      // メッセージ送信
      const sentMessage = await messages.sendMessage(
        content,
        session.currentExpert,
        session.session.id,
        uploadedFiles.map(uf => uf.file)
      );

      if (sentMessage && callbacks?.onMessageSent) {
        callbacks.onMessageSent(sentMessage);
      }

      return sentMessage;
    } catch (error) {
      if (callbacks?.onError) {
        callbacks.onError({
          type: 'network',
          message: 'メッセージの送信に失敗しました',
          details: error,
        });
      }
      return null;
    }
  }, [session.session, session.currentExpert, messages, fileUpload, callbacks]);

  // 新しいセッション開始
  const startNewSession = useCallback(async (expert: AIExpert) => {
    const newSession = await session.startNewSession(expert);
    if (newSession) {
      messages.clearMessages();
      if (callbacks?.onSessionStarted) {
        callbacks.onSessionStarted(newSession);
      }
    }
    return newSession;
  }, [session, messages, callbacks]);

  // エラー統一処理
  const clearError = useCallback(() => {
    messages.clearError();
    session.clearError();
    fileUpload.clearError();
  }, [messages, session, fileUpload]);

  // 統合された状態
  const combinedState = {
    // セッション状態
    session: session.session,
    currentExpert: session.currentExpert,
    
    // メッセージ状態
    messages: messages.messages,
    messageStatuses: messages.messageStatuses,
    
    // ローディング状態
    isLoading: session.isLoading || messages.isLoading || fileUpload.isUploading,
    isTyping: messages.isTyping,
    
    // 中断機能状態
    currentRequestId: messages.currentRequestId,
    isAborting: messages.isAborting,
    
    // ファイルアップロード状態
    uploadProgress: fileUpload.uploadProgress,
    isUploading: fileUpload.isUploading,
    
    // エラー状態（最新のエラーを優先）
    error: messages.error || session.error || fileUpload.error,
  };

  // 統合されたアクション
  const combinedActions = {
    sendMessage,
    startNewSession,
    retryMessage: messages.retryMessage,
    regenerateAIMessage: messages.regenerateAIMessage,
    abortMessage: messages.abortMessage,
    retryAbortedMessage: messages.retryAbortedMessage,
    loadChatHistory: session.loadChatHistory,
    loadMessages: messages.loadMessages, // メッセージ履歴読み込み機能を追加
    endSession: session.endSession,
    uploadFiles: fileUpload.uploadFiles,
    cancelUpload: fileUpload.cancelUpload,
    clearError,
    clearMessages: messages.clearMessages,
  };

  return {
    ...combinedState,
    ...combinedActions,
  };
};